<template>

    <div class="page-admin-cloning page-main">
        <slot></slot>
        <div class="page-main__content">
            <div class="app-header">
                <div class="app-header__cta">
                    <app-button
                        @click="isCreating = 'import'"
                        theme="secondary"
                        label="Generate Import Token"
                        :disabled="isCreating">
                    </app-button>
                    <app-button
                        @click="isCreating = 'export'"
                        theme="primary"
                        :disabled="isCreating"
                        label="Begin Export Process">
                    </app-button>
                </div>
            </div>

            <div v-if="isCreating">
                <form v-if="isCreating === 'import'" class="page-main__section grid">
                    <h3 class="page-main__section-title col-12">New Import Token</h3>
                    <div class="col-12 col-sm-9">
                        <div class="input select">
                            <label for="client">Target Client</label>
                            <select id="client" v-model="selectedClient" class="form-control" required>
                                <option :value="null" disabled selected>Select a client...</option>
                                <option v-for="client in clients"
                                    :key="`client-${client.id}`"
                                    :value="client.id">
                                    {{ client.name }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="col-12 col-sm-3">
                        <div class="page-main__buttons">
                            <app-button
                                theme="secondary"
                                label="Cancel"
                                @click="isCreating = false">
                            </app-button>
                            <app-button
                                theme="primary"
                                @click="createImportProcess()">
                                Save
                            </app-button>
                        </div>
                    </div>
                </form>
                <form v-else-if="isCreating === 'export'" class="page-main__section grid">
                    <h3 class="page-main__section-title col-12">New Export Process</h3>
                    <div class="col-12 col-md-8 col-lg-9">
                        <form class="grid" :disabled="isConnecting">
                            <div class="col-12 col-sm-4">
                                <div class="input select">
                                    <label for="client">Source Client</label>
                                    <select id="client" v-model="selectedClient" class="form-control" required>
                                        <option :value="null" disabled selected>Select a client...</option>
                                        <option v-for="client in clients"
                                            :key="`client-${client.id}`"
                                            :value="client.id">
                                            {{ client.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <div class="col-12 col-sm-4">
                                <div class="input text">
                                    <label for="url">Target URL</label>
                                    <input id="url" type="text" v-model="targetUrl" class="form-control" required>
                                </div>
                            </div>
                            <div class="col-12 col-sm-4">
                                <div class="input text">
                                    <label for="token">Target Import Token</label>
                                    <input id="token" type="text" v-model="importToken" class="form-control" required>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div class="col-12 col-md-4 col-lg-3">
                        <div class="page-main__buttons">
                            <app-button
                                theme="secondary"
                                label="Cancel"
                                :disabled="isConnecting"
                                @click="isCreating = false">
                            </app-button>
                            <app-button
                                theme="primary"
                                label="Connect"
                                :disabled="isConnecting"
                                @click="createExportProcess()">
                            </app-button>
                        </div>
                    </div>
                </form>
                <app-error
                    class="mt-3"
                    v-if="errors.length"
                    v-bind="errors" />
            </div>

            <clone-process-item
                v-for="process in processes"
                :key="`clone-process-${process.id}`"
                :source="process"
                :status="processingStatus[process.id]"
                @start="startProcess(process)"
                @restart="startProcess(process, true)"
                @cancel="cancelProcess(process)"
                @remove="removeProcess(process)"
                @set-preferences="setPreferences(process, pref)"
                @update="updateProcess"
                class="page-admin-cloning__list page-main__section">
                <a :name="process.id" class="hidden"></a>
            </clone-process-item>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import CloneProcessItem from './CloneProcessItem'
import Echo from '../helpers/echo'
import api from '../api/cloning'

export default {
    components: { CloneProcessItem },
    props: {
        items: {
            type: Array,
            default: () => []
        },
        clients: {
            type: Array,
            default: () => []
        }
    },
    data () {
        return {
            isLoading: false,
            isCreating: false,
            isConnecting: false,
            processingStatus: { },
            filterType: null,
            selectedClient: null,
            targetUrl: null,
            importToken: null,
            filterTypes: [{
                'All': null,
                'Import': 'target_url',
                'Export': 'source_url'
            }],
            processes: this.items,
            preferences: { },
            errors: []
        }
    },
    computed: {
        filtered () {
            if (!this.filterType) {
                return this.processes
            }

            return _.reject(this.processes, { [this.filterType]: null })
        }
    },
    methods: {
        async updateItems () {
            this.isLoading = true
            const items = await api.getProcesses()
            if (items) {
                this.processes = items
            }
            this.isLoading = false
        },
        async createImportProcess () {
            this.isLoading = true

            const result = await api.createProcess({
                client_id: this.selectedClient
            })

            console.log(result)

            this.isCreating = false
            this.isLoading = false

            this.updateItems()
        },
        async createExportProcess () {
            this.isLoading = true
            this.isConnecting = true

            const result = await api.createProcess({
                client_id: this.selectedClient,
                target_url: this.targetUrl,
                token: this.importToken
            })

            console.log(result)

            this.isConnecting = false
            this.isLoading = false

            if (result.errors) {
                this.errors = result.errors
            } else {
                this.isCreating = false
            }

            this.updateItems()
        },
        async startProcess (process, force = false) {
            let result
            if (process.target_url) {
                result = await api.sendPayload(process.id, force)
            } else {
                result = await api.start(process.id, force)
            }
            console.log(result)
            this.updateProcess(result)
        },
        async cancelProcess (process) {
            const result = await api.cancel(process.id)
            console.log(result)
            this.updateProcess(result)
        },
        async removeProcess (process) {
            if (!confirm('Are you sure you want to remove this process?')) {
                return
            }

            const result = await api.remove(process.id)
            if (result) {
                const index = this.processes.indexOf(process)
                this.processes.splice(index, 1)
            }
            console.log(result)
        },
        updateProcess (process) {
            const index = _.findIndex(this.processes, { id: process.id })
            console.log(process)
            if (index >= 0) {
                this.processes.splice(index, 1, {
                    ...this.processes[index],
                    ...process
                })
            }
        },
        setupEchoListener (process) {
            // subscribe to channel for each process
            Echo.private(`App.Models.CloneProcess.${process.id}`)
                .notification((notification) => {
                    this.processingStatus[process.id] = notification
                    const model = _.get(notification, 'model', { })
                    this.updateProcess(model)
                })
        }
    },
    created () {
        const status = { }
        this.processes.forEach((process) => {
            // set initial status
            status[process.id] = {
                text: process.status,
            }
            if (process.batch) {
                status[process.id].progress = process.batch.progress
            }
            this.setupEchoListener(process)
        })
        this.processingStatus = status
    }
}
</script>
